import { useAtomQueryRunner } from "../state/use-atom-query-runner";
import { squashAtomCommandFailure } from "@amoreg/client-runtime/state/runtime";
import { resolveAssetUrl } from "@amoreg/client-runtime/state/assets";
import { withAssetRevision } from "./assetRevision";
import { useAtomValue } from "@effect/atom-react";
import { assetUrlStateFromResult, type AssetUrlState } from "@amoreg/client-runtime/state/assets";
import type { AssetResource, EnvironmentId } from "@amoreg/contracts";
import { useCallback, useMemo } from "react";

import { assetEnvironment } from "~/state/assets";
import { usePreparedConnection } from "~/state/session";

export { resolveAssetUrl } from "@amoreg/client-runtime/state/assets";

export type { AssetUrlState } from "@amoreg/client-runtime/state/assets";

export function useAssetUrlState(
  environmentId: EnvironmentId,
  resource: AssetResource,
  revision?: string,
): AssetUrlState {
  const preparedConnection = usePreparedConnection(environmentId);
  const result = useAtomValue(
    assetEnvironment.createUrl({
      environmentId,
      input: { resource },
    }),
  );
  const state = assetUrlStateFromResult(
    result,
    preparedConnection._tag === "Some" ? preparedConnection.value.httpBaseUrl : null,
  );
  return state._tag === "Success"
    ? { ...state, url: withAssetRevision(state.url, revision) }
    : state;
}

export function useAssetUrl(environmentId: EnvironmentId, resource: AssetResource): string | null {
  const result = useAssetUrlState(environmentId, resource);
  if (result._tag !== "Success") {
    return null;
  }
  return result.url;
}

export function useAssetUrlStates(
  environmentId: EnvironmentId,
  resources: ReadonlyArray<AssetResource>,
): ReadonlyArray<AssetUrlState> {
  const preparedConnection = usePreparedConnection(environmentId);
  const results = useAtomValue(
    assetEnvironment.createUrls({
      environmentId,
      resources,
    }),
  );
  return useMemo(
    () =>
      results.map((result) =>
        assetUrlStateFromResult(
          result,
          preparedConnection._tag === "Some" ? preparedConnection.value.httpBaseUrl : null,
        ),
      ),
    [preparedConnection, results],
  );
}

export function useAssetUrls(
  environmentId: EnvironmentId,
  resources: ReadonlyArray<AssetResource>,
): ReadonlyArray<string | null> {
  const states = useAssetUrlStates(environmentId, resources);
  return useMemo(
    () => states.map((state) => (state._tag === "Success" ? state.url : null)),
    [states],
  );
}

/** Reauthorize previews and downloads instead of reopening an expired cached claim. */
export function useAssetUrlRefresh(
  environmentId: EnvironmentId | null,
  resource: AssetResource | null,
  disposition: "inline" | "attachment" = "inline",
) {
  const connection = usePreparedConnection(environmentId);
  const baseUrl = connection._tag === "Some" ? connection.value.httpBaseUrl : null;
  const refresh = useAtomQueryRunner(assetEnvironment.createUrl, {
    reportFailure: false,
    refresh: true,
  });
  return useCallback(
    async (signal?: AbortSignal) => {
      if (!environmentId || !resource || !baseUrl) return null;
      const result = await refresh({ environmentId, input: { resource, disposition } }, signal);
      if (result._tag === "Failure") throw squashAtomCommandFailure(result);
      return resolveAssetUrl(baseUrl, result.value.relativeUrl);
    },
    [environmentId, resource, baseUrl, disposition, refresh],
  );
}
